import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, Star, Upload, Trash2, Plus } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import ImageDropZone from '@/components/admin/ImageDropZone';
import EnhanceableImage from '@/components/admin/EnhanceableImage';

const EMPTY = {
  name: '', slug: '', category: 'gt', short_description: '', full_description: '',
  hero_image_url: '', hero_video_url: '', gallery_images: [], showcase_video_url: '',
  overall_rating: 0, rating_realism: 0, rating_immersion: 0, rating_hardware: 0,
  rating_comfort: 0, rating_beginner_friendly: 0, rating_competitive: 0,
  experience_text: '', spec_cockpit: '', spec_seat: '', spec_wheelbase: '', spec_pedals: '',
  spec_steering_wheel: '', spec_dashboard: '', spec_displays: '', spec_pc: '', spec_audio: '',
  supported_games: [], booking_url: '', featured: false, sort_order: 0,
};

const CATEGORIES = [
  { key: 'gt', label: 'GT' },
  { key: 'formula', label: 'Formula' },
  { key: 'rally', label: 'Rally' },
  { key: 'drift', label: 'Drift' },
  { key: 'other', label: 'Other' },
];

const RATINGS = [
  { key: 'overall_rating', label: 'Overall' },
  { key: 'rating_realism', label: 'Realism' },
  { key: 'rating_immersion', label: 'Immersion' },
  { key: 'rating_hardware', label: 'Hardware Quality' },
  { key: 'rating_comfort', label: 'Comfort' },
  { key: 'rating_beginner_friendly', label: 'Beginner Friendly' },
  { key: 'rating_competitive', label: 'Competitive Racing' },
];

const SPECS = [
  { key: 'spec_cockpit', label: 'Cockpit' },
  { key: 'spec_seat', label: 'Seat' },
  { key: 'spec_wheelbase', label: 'Wheelbase' },
  { key: 'spec_pedals', label: 'Pedals' },
  { key: 'spec_steering_wheel', label: 'Steering Wheel' },
  { key: 'spec_dashboard', label: 'Dashboard' },
  { key: 'spec_displays', label: 'Displays' },
  { key: 'spec_pc', label: 'PC Specifications' },
  { key: 'spec_audio', label: 'Audio System' },
];

const cls = "bg-white/5 border-white/10 text-white h-10 placeholder:text-white/20 focus:border-primary/60";
const labelCls = "text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block";

function slugify(s) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

export default function ShowcaseRigFormModal({ rig, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [gameInput, setGameInput] = useState('');
  const [galleryInput, setGalleryInput] = useState('');
  const isEdit = !!rig;

  useEffect(() => {
    if (rig) setForm({ ...EMPTY, ...rig, gallery_images: rig.gallery_images || [], supported_games: rig.supported_games || [] });
  }, [rig]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleNameChange = (name) => {
    set('name', name);
    if (!isEdit) set('slug', slugify(name));
  };

  const handleImageUpload = async (file, field) => {
    try {
      const { file_url } = await base44.integrations.Core.UploadFile({ file });
      if (field === 'gallery') {
        set('gallery_images', [...(form.gallery_images || []), file_url]);
      } else {
        set(field, file_url);
      }
    } catch (e) {
      setError('Image upload failed.');
    }
  };

  const addGame = () => {
    const g = gameInput.trim();
    if (!g) return;
    set('supported_games', [...(form.supported_games || []), g]);
    setGameInput('');
  };

  const removeGame = (idx) => set('supported_games', (form.supported_games || []).filter((_, i) => i !== idx));

  const addGalleryUrl = () => {
    const u = galleryInput.trim();
    if (!u) return;
    set('gallery_images', [...(form.gallery_images || []), u]);
    setGalleryInput('');
  };
  const removeGallery = (idx) => set('gallery_images', (form.gallery_images || []).filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.slug || !form.category) { setError('Name, slug and category are required.'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = { ...form, slug: slugify(form.slug) };
      // coerce numeric ratings
      RATINGS.forEach((r) => { payload[r.key] = Number(payload[r.key] || 0); });
      payload.sort_order = Number(payload.sort_order || 0);
      if (isEdit) await base44.entities.ShowcaseRig.update(rig.id, payload);
      else await base44.entities.ShowcaseRig.create(payload);
      onSaved();
    } catch (err) {
      setError(err.message || 'Failed to save rig.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      >
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
          className="bg-[#111115] border border-white/10 w-full max-w-3xl my-8"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#111115] z-10">
            <div>
              <p className="text-primary text-[10px] font-bold tracking-widest mb-0.5">{isEdit ? 'EDIT' : 'NEW'} SIMULATOR</p>
              <h2 className="text-white font-black text-lg uppercase">{form.name || 'Untitled Rig'}</h2>
            </div>
            <button onClick={onClose} className="text-white/40 hover:text-white p-1"><X className="w-5 h-5" /></button>
          </div>

          <form onSubmit={handleSubmit} className="p-5 space-y-6">
            {/* Basics */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className={labelCls}>RIG NAME *</Label>
                <Input value={form.name} onChange={(e) => handleNameChange(e.target.value)} className={cls} placeholder="Porsche 992 Cup Simulator" />
              </div>
              <div>
                <Label className={labelCls}>SLUG *</Label>
                <Input value={form.slug} onChange={(e) => set('slug', slugify(e.target.value))} className={cls} placeholder="porsche-992-cup" />
              </div>
              <div>
                <Label className={labelCls}>CATEGORY *</Label>
                <select value={form.category} onChange={(e) => set('category', e.target.value)}
                  className="w-full bg-white/5 border border-white/10 text-white h-10 px-3 focus:border-primary/60">
                  {CATEGORIES.map((c) => <option key={c.key} value={c.key} className="bg-[#111115]">{c.label}</option>)}
                </select>
              </div>
              <div>
                <Label className={labelCls}>SHORT DESCRIPTION</Label>
                <Input value={form.short_description} onChange={(e) => set('short_description', e.target.value)} className={cls} placeholder="One-line tagline" />
              </div>
            </div>

            <div>
              <Label className={labelCls}>FULL DESCRIPTION</Label>
              <textarea value={form.full_description} onChange={(e) => set('full_description', e.target.value)} rows={3}
                className="w-full bg-white/5 border border-white/10 text-white p-3 text-sm placeholder:text-white/20 focus:border-primary/60" placeholder="Overview text shown on the page" />
            </div>

            {/* Media */}
            <div className="border-t border-white/10 pt-5">
              <p className="text-primary text-[10px] font-bold tracking-widest mb-3">MEDIA</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
                <div>
                  <Label className={labelCls}>HERO IMAGE URL</Label>
                  <Input value={form.hero_image_url} onChange={(e) => set('hero_image_url', e.target.value)} className={cls} placeholder="https://..." />
                </div>
                <div>
                  <Label className={labelCls}>HERO VIDEO URL (MP4)</Label>
                  <Input value={form.hero_video_url} onChange={(e) => set('hero_video_url', e.target.value)} className={cls} placeholder="https://...mp4 (optional bg video)" />
                </div>
              </div>
              {form.hero_image_url ? (
                <div className="flex items-center gap-3 mb-4">
                  <EnhanceableImage src={form.hero_image_url} onChange={(url) => set('hero_image_url', url)} onDelete={() => set('hero_image_url', '')} className="w-32 h-20" alt="Hero" />
                  <label className="flex items-center gap-2 text-white/40 text-xs cursor-pointer hover:text-white/70">
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && handleImageUpload(e.target.files[0], 'hero_image_url')} />
                    <span className="inline-flex items-center gap-1.5 border border-white/10 px-3 py-1.5"><Upload className="w-3.5 h-3.5" /> REPLACE HERO</span>
                  </label>
                </div>
              ) : (
                <label className="flex items-center gap-2 text-white/40 text-xs cursor-pointer mb-4 hover:text-white/70">
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && handleImageUpload(e.target.files[0], 'hero_image_url')} />
                  <span className="inline-flex items-center gap-1.5 border border-white/10 px-3 py-1.5"><Upload className="w-3.5 h-3.5" /> UPLOAD HERO</span>
                </label>
              )}

              <Label className={labelCls}>GALLERY IMAGES</Label>
              <ImageDropZone compact label="DROP PHOTOS HERE OR CLICK TO BROWSE"
                existing={form.gallery_images || []}
                onUploaded={(url) => set('gallery_images', [...(form.gallery_images || []), url])} />
              <div className="flex gap-2 mb-2 mt-2">
                <Input value={galleryInput} onChange={(e) => setGalleryInput(e.target.value)} className={cls} placeholder="Or paste image URL" />
                <button type="button" onClick={addGalleryUrl} className="bg-white/5 border border-white/10 hover:border-primary/40 text-white/70 hover:text-white px-3 text-xs font-bold">ADD</button>
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && handleImageUpload(e.target.files[0], 'gallery')} />
                  <span className="inline-flex items-center gap-1.5 border border-white/10 px-3 py-2 text-white/50 hover:text-white"><Upload className="w-3.5 h-3.5" /></span>
                </label>
              </div>
              <div className="flex flex-wrap gap-2">
                {(form.gallery_images || []).map((img, i) => (
                  <EnhanceableImage key={i} src={img} alt="Gallery"
                    onChange={(url) => set('gallery_images', (form.gallery_images || []).map((v, idx) => (idx === i ? url : v)))}
                    onDelete={() => removeGallery(i)} />
                ))}
              </div>

              <div className="mt-3">
                <Label className={labelCls}>SHOWCASE VIDEO URL (YOUTUBE / VIMEO)</Label>
                <Input value={form.showcase_video_url} onChange={(e) => set('showcase_video_url', e.target.value)} className={cls} placeholder="https://youtube.com/watch?v=..." />
              </div>
            </div>

            {/* Ratings */}
            <div className="border-t border-white/10 pt-5">
              <p className="text-primary text-[10px] font-bold tracking-widest mb-3">RATINGS (0–10)</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {RATINGS.map((r) => (
                  <div key={r.key}>
                    <Label className={labelCls}>{r.label}</Label>
                    <Input type="number" min="0" max="10" step="0.1" value={form[r.key]}
                      onChange={(e) => set(r.key, e.target.value)} className={cls} />
                  </div>
                ))}
              </div>
            </div>

            {/* Specs */}
            <div className="border-t border-white/10 pt-5">
              <p className="text-primary text-[10px] font-bold tracking-widest mb-3">TECHNICAL SPECIFICATIONS</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {SPECS.map((s) => (
                  <div key={s.key}>
                    <Label className={labelCls}>{s.label}</Label>
                    <Input value={form[s.key]} onChange={(e) => set(s.key, e.target.value)} className={cls} />
                  </div>
                ))}
              </div>
            </div>

            {/* Experience + Games */}
            <div className="border-t border-white/10 pt-5">
              <p className="text-primary text-[10px] font-bold tracking-widest mb-3">EXPERIENCE & GAMES</p>
              <div className="mb-4">
                <Label className={labelCls}>EXPERIENCE TEXT</Label>
                <textarea value={form.experience_text} onChange={(e) => set('experience_text', e.target.value)} rows={3}
                  className="w-full bg-white/5 border border-white/10 text-white p-3 text-sm focus:border-primary/60" placeholder="What makes this rig unique..." />
              </div>
              <Label className={labelCls}>SUPPORTED GAMES</Label>
              <div className="flex gap-2 mb-2">
                <Input value={gameInput} onChange={(e) => setGameInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addGame())}
                  className={cls} placeholder="Add a game title" />
                <button type="button" onClick={addGame} className="bg-white/5 border border-white/10 hover:border-primary/40 text-white/70 hover:text-white px-3 text-xs font-bold inline-flex items-center gap-1">
                  <Plus className="w-3.5 h-3.5" /> ADD
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {(form.supported_games || []).map((g, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 bg-primary/10 border border-primary/20 text-primary text-xs font-bold px-2.5 py-1">
                    {g}
                    <button type="button" onClick={() => removeGame(i)} className="text-primary/60 hover:text-primary"><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            </div>

            {/* Settings */}
            <div className="border-t border-white/10 pt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label className={labelCls}>BOOKING URL</Label>
                <Input value={form.booking_url} onChange={(e) => set('booking_url', e.target.value)} className={cls} placeholder="/bookings" />
              </div>
              <div>
                <Label className={labelCls}>SORT ORDER</Label>
                <Input type="number" value={form.sort_order} onChange={(e) => set('sort_order', e.target.value)} className={cls} />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input type="checkbox" checked={form.featured} onChange={(e) => set('featured', e.target.checked)} className="w-4 h-4 accent-primary" />
                  <span className="text-white/60 text-xs font-bold tracking-wide">FEATURED ON HOMEPAGE</span>
                </label>
              </div>
            </div>

            {error && <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 px-4 py-2">{error}</p>}

            <div className="flex gap-3 pt-2">
              <button type="submit" disabled={saving}
                className="flex-1 bg-primary hover:bg-primary/90 text-white font-black text-xs tracking-widest py-3.5 transition-colors disabled:opacity-40 flex items-center justify-center gap-2">
                {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> SAVING...</> : <>{isEdit ? 'SAVE CHANGES' : 'CREATE RIG'}</>}
              </button>
              <button type="button" onClick={onClose} className="px-5 border border-white/10 text-white/60 hover:text-white text-xs font-bold tracking-widest">
                CANCEL
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}